import Box from '@mui/material/Box'
import CardActions from '@mui/material/CardActions'
import Skeleton from '@mui/material/Skeleton'
import React from 'react'
import { useSelector } from 'react-redux'
import { z } from 'zod'

import Flag from '@/app/component/atom/flag'
import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import { isPhone } from '@/app/component/form/utils/validation'
import { selectDefaultCountryCode } from '@/app/module/utils/store/selectors'
import { addPlusToPhoneNumber } from '@/app/service/util/format'
import { esCountries } from '@/config/resources'

export function OrganizationFormSkeleton() {
  return (
    <>
      <Skeleton variant="rectangular" width="100%" height={38} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={38} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={38} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={38} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={38} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" width="100%" height={38} sx={{ mb: 2, borderRadius: 1 }} />
      <Skeleton variant="rectangular" width={150} height={38} sx={{ mb: 2, ml: 'auto', borderRadius: 1 }} />
    </>
  )
}

type CountryOption = {
  value: string
  label: string
  code: string
}

const countryOptions: CountryOption[] = esCountries.map(({ name, code }) => ({ value: name, label: name, code }))

const orgCreateSchema = z.object({
  name: z.string().min(1, { message: 'Required field' }).describe('Name'),
  phone: z.string().optional().describe('Phone number').transform(isPhone(false)),
  address: z.string().optional().describe('Address'),
  country: z.string().optional().describe('Country'),
  location: z.string().optional().describe('Location'),
  website: z.string().optional().describe('Website'),
})

type OrganizationData = z.infer<typeof orgCreateSchema>

type Props = {
  isLoading?: boolean
  submitLabel: string
  defaultValues?: OrganizationData
  onSubmit: (data: OrganizationData) => Promise<boolean>
}

const OrganizationForm: React.FC<Props> = ({ defaultValues, isLoading, submitLabel, onSubmit }) => {
  const countryCode = useSelector(selectDefaultCountryCode)
  const handleSubmit = async (data: OrganizationData) => {
    const phoneNumber = data.phone ? data.phone.replace(/\+/g, '') : undefined
    const body = {
      name: data.name,
      address: data.address || '',
      country: data.country || '',
      location: data.location || '',
      website: data.website || '',
      phone: phoneNumber || '',
    }
    onSubmit(body)
  }

  const values = {
    ...defaultValues,
    phone: addPlusToPhoneNumber(defaultValues?.phone || ''),
  }

  return (
    <Form
      defaultValues={values}
      fields={{
        name: {
          type: 'text',
          fieldProps: {
            label: 'Name',
            name: 'name',
            required: true,
          },
        },
        phone: {
          type: 'tel',
          fieldProps: {
            label: 'Phone number',
            name: 'phone',
            defaultCountry: countryCode,
          },
        },
        address: {
          type: 'text',
          fieldProps: {
            label: 'Address',
            name: 'address',
          },
        },
        country: {
          type: 'autocomplete',
          fieldProps: {
            label: 'Country',
            options: countryOptions,
            name: 'country',
            renderOption: (itemProps, option) => {
              const { code, label } = option as CountryOption
              return (
                <Box component="li" {...itemProps} sx={{ display: 'flex', gap: '0.5rem' }}>
                  <Flag code={code} />
                  <span>{label}</span>
                </Box>
              )
            },
          },
        },
        location: {
          type: 'text',
          fieldProps: {
            label: 'Location',
            name: 'location',
          },
        },
        website: {
          type: 'text',
          fieldProps: {
            label: 'Website',
            name: 'website',
          },
        },
      }}
      schema={orgCreateSchema}
      shouldReset
      onSubmit={handleSubmit}
      renderActions={({ isDirty }) => (
        <CardActions sx={{ p: 0 }}>
          <SubmitButton isDirty={isDirty} isSubmitting={!!isLoading} type="submit" variant="contained">
            {submitLabel}
          </SubmitButton>
        </CardActions>
      )}
    />
  )
}

export default OrganizationForm

export type { OrganizationData, Props as OrganizationFormProps }
export { orgCreateSchema }
